import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { DEVICE_OWNER, readOwned, writeOwned } from '../utils/ownerStorage';

/** Whose browser storage is in use: an account id, the device, or null until sign-in state is known. */
export function useStorageOwner() {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated && user?.id ? user.id : DEVICE_OWNER;
}

/**
 * State remembered in the browser for whoever is signed in. `restore` turns
 * what was saved (or null) into a value, and must be the same function on
 * every render. The value and its owner are held together, so one person's
 * value is never written under another's name when the account changes.
 */
export function useOwnedState(base, restore) {
  const owner = useStorageOwner();
  const load = () => ({ owner, value: restore(owner ? readOwned(base, owner) : null) });
  const [held, setHeld] = useState(load);
  let current = held;
  if (held.owner !== owner) {
    current = load();
    setHeld(current);
  }

  useEffect(() => {
    if (held.owner) writeOwned(base, held.owner, held.value);
  }, [base, held]);

  const setValue = useCallback((update) => {
    setHeld((now) => {
      const value = typeof update === 'function' ? update(now.value) : update;
      return value === now.value ? now : { owner: now.owner, value };
    });
  }, []);

  return [current.value, setValue];
}
